import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  FileText,
  Settings,
  Menu,
  X,
  LogOut,
  ClipboardList,
  Truck,
  FileSpreadsheet,
  DollarSign,
  Shield,
  Building2,
  Navigation,
  Radio,
  ChevronLeft,
  ChevronRight,
  Receipt
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { FirebaseAuthService } from '../services/firebaseAuthService';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

import { AppPermission } from '../types';
import { usePermissionsContext } from '../contexts/PermissionsContext';
import { empresaService } from '../services/empresaService';
import { EmpresaConfig } from '../types/empresa';
import { PWAInstallBanner } from './pwa/PWAInstallBanner';

const navigation: { name: string; href: string; icon: any; permission?: AppPermission; badge?: string }[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, permission: 'view_dashboard' },
  { name: 'Agenda & Planner', href: '/agenda', icon: CalendarDays, permission: 'view_orders', badge: 'Fretes' },
  { name: 'Painel Operacional', href: '/operacional', icon: Radio, badge: 'GPS' },
  { name: 'Cadastros', href: '/pessoas', icon: Users, permission: 'view_people' },
  { name: 'Veículos', href: '/veiculos', icon: Truck, permission: 'view_vehicles' },
  { name: 'Ordens de Serviço', href: '/os', icon: FileText, permission: 'view_orders' },
  { name: 'Medições', href: '/medicoes', icon: FileSpreadsheet, permission: 'view_medicoes' },
  { name: 'Financeiro', href: '/financeiro', icon: DollarSign, permission: 'view_financial' },
  { name: 'Documentos Fiscais', href: '/fiscal', icon: Receipt, permission: 'view_fiscal', badge: 'CT-e / NFS-e' },
  { name: 'Propostas', href: '/propostas', icon: ClipboardList, permission: 'view_orders' },
  { name: 'Área do Cliente', href: '/area-cliente', icon: Building2, badge: 'Portal' },
  { name: 'Área do Motorista', href: '/area-motorista', icon: Navigation, badge: 'Portal' },
  { name: 'Sistema', href: '/sistema', icon: Settings, permission: 'manage_users' },
];

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem('voope_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [empresaAtiva, setEmpresaAtiva] = React.useState<EmpresaConfig | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissionsContext();

  React.useEffect(() => {
    empresaService.getEmpresaAtiva().then(setEmpresaAtiva);

    const handleEmpresaUpdate = () => {
      empresaService.getEmpresaAtiva().then(setEmpresaAtiva);
    };

    window.addEventListener('empresa_config_updated', handleEmpresaUpdate);
    return () => window.removeEventListener('empresa_config_updated', handleEmpresaUpdate);
  }, []);

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('voope_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const filteredNavigation = navigation.filter(item => !item.permission || hasPermission(item.permission));

  const handleLogout = async () => {
    await FirebaseAuthService.logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Mobile sidebar (Drawer) */}
      <div className={cn("fixed inset-0 z-50 lg:hidden", sidebarOpen ? "block" : "hidden")}>
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-xs transition-opacity" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-slate-900 shadow-2xl flex flex-col z-10 animate-in slide-in-from-left duration-200">
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800">
            {empresaAtiva?.logo_url ? (
              <img
                src={empresaAtiva.logo_url}
                alt={empresaAtiva.nome_fantasia || 'Empresa'}
                className="max-h-9 max-w-[170px] object-contain"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="text-xl font-bold text-white uppercase tracking-tight">
                {empresaAtiva?.nome_fantasia || 'Voope'}{' '}
                <span className="text-red-600">{empresaAtiva?.nome_fantasia ? '' : 'TWO'}</span>
              </span>
            )}
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
              aria-label="Fechar menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-xl group transition-all",
                    isActive ? "bg-red-600 text-white shadow-md shadow-red-600/30" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )}
                >
                  <div className="flex items-center">
                    <item.icon className={cn("mr-3 flex-shrink-0 h-5 w-5", isActive ? "text-white" : "text-slate-400 group-hover:text-slate-200")} />
                    <span>{item.name}</span>
                  </div>
                  {item.badge && (
                    <span className={cn(
                      "text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full border",
                      isActive
                        ? "bg-red-700/80 text-white border-red-500/50"
                        : "bg-slate-800 text-slate-400 border-slate-700"
                    )}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-slate-800 space-y-2">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-red-400 rounded-xl hover:bg-slate-800 hover:text-red-300 transition-colors cursor-pointer"
            >
              <LogOut className="mr-3 flex-shrink-0 h-5 w-5" />
              Sair do Sistema
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar (Collapsible) */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div
          className={cn(
            "flex flex-col bg-slate-900 border-r border-slate-800 transition-all duration-200 relative",
            isCollapsed ? "w-20" : "w-64"
          )}
        >
          {/* Header do Sidebar */}
          <div className={cn("flex items-center h-16 border-b border-slate-800 px-3", isCollapsed ? "justify-center" : "justify-between")}>
            {!isCollapsed ? (
              empresaAtiva?.logo_url ? (
                <img
                  src={empresaAtiva.logo_url}
                  alt={empresaAtiva.nome_fantasia || 'Empresa'}
                  className="max-h-9 max-w-[170px] object-contain"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="text-lg font-bold text-white tracking-tight uppercase truncate">
                  {empresaAtiva?.nome_fantasia || 'Voope'}{' '}
                  <span className="text-red-600">{empresaAtiva?.nome_fantasia ? '' : 'TWO'}</span>
                </span>
              )
            ) : (
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-red-600 to-red-500 flex items-center justify-center text-white font-black text-lg shadow-md shadow-red-600/30">
                V
              </div>
            )}
          </div>

          {/* Lista de Navegação */}
          <div className="flex flex-col flex-1 overflow-y-auto py-3">
            <nav className={cn("flex-1 space-y-1.5", isCollapsed ? "px-2" : "px-3")}>
              {filteredNavigation.map((item) => {
                const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    title={isCollapsed ? item.name : undefined}
                    className={cn(
                      "flex items-center text-sm font-medium rounded-xl group transition-all",
                      isCollapsed
                        ? "justify-center p-3 relative"
                        : "justify-between px-3 py-2.5",
                      isActive
                        ? "bg-red-600 text-white shadow-md shadow-red-600/30"
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    )}
                  >
                    <div className="flex items-center">
                      <item.icon
                        className={cn(
                          "flex-shrink-0 h-5 w-5",
                          !isCollapsed && "mr-3",
                          isActive ? "text-white" : "text-slate-400 group-hover:text-slate-200"
                        )}
                      />
                      {!isCollapsed && <span className="truncate">{item.name}</span>}
                    </div>

                    {!isCollapsed && item.badge && (
                      <span className={cn(
                        "text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-md border",
                        isActive
                          ? "bg-red-700/80 text-white border-red-500/50"
                          : "bg-slate-800 text-slate-400 border-slate-700"
                      )}>
                        {item.badge}
                      </span>
                    )}

                    {isCollapsed && item.badge && (
                      <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500 ring-2 ring-slate-900" />
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Rodapé com botão de recolher e logout */}
          <div className="p-3 border-t border-slate-800 space-y-1.5 bg-slate-900/60">
            <button
              onClick={toggleCollapse}
              className={cn(
                "flex items-center w-full py-2 text-xs font-semibold text-slate-400 rounded-lg hover:bg-slate-800 hover:text-slate-200 transition-colors cursor-pointer",
                isCollapsed ? "justify-center px-2" : "px-3"
              )}
              title={isCollapsed ? "Expandir menu lateral" : "Recolher menu lateral"}
            >
              {isCollapsed ? (
                <ChevronRight className="w-5 h-5 text-slate-400" />
              ) : (
                <>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  <span>Recolher Menu</span>
                </>
              )}
            </button>

            <button
              onClick={handleLogout}
              className={cn(
                "flex items-center w-full py-2 text-xs font-semibold text-red-400 rounded-lg hover:bg-slate-800 hover:text-red-300 transition-colors cursor-pointer",
                isCollapsed ? "justify-center px-2" : "px-3"
              )}
              title="Sair do sistema"
            >
              <LogOut className={cn("flex-shrink-0 h-4 w-4", !isCollapsed && "mr-2")} />
              {!isCollapsed && <span>Sair</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="flex flex-col flex-1 overflow-hidden min-w-0">
        <PWAInstallBanner />
        
        {/* Top Header Mobile / Tablet */}
        <header className="flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4 bg-white border-b border-slate-200 lg:hidden shadow-xs shrink-0 z-30">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
              aria-label="Abrir menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            {empresaAtiva?.logo_url ? (
              <img
                src={empresaAtiva.logo_url}
                alt={empresaAtiva.nome_fantasia || 'Empresa'}
                className="max-h-8 max-w-[140px] object-contain"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="text-base sm:text-lg font-bold text-slate-900 tracking-tight uppercase truncate">
                {empresaAtiva?.nome_fantasia || 'Voope'}{' '}
                <span className="text-red-600">{empresaAtiva?.nome_fantasia ? '' : 'TWO'}</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
              title="Sair"
              aria-label="Sair do sistema"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Área Central de Conteúdo */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-5 md:p-6 lg:p-8 pb-24 lg:pb-8 min-w-0">
          <Outlet />
        </main>

        {/* Barra de Navegação Rápida Inferior (Mobile / Smartphone) */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-slate-900/95 backdrop-blur-md border-t border-slate-800 z-40 flex items-center justify-around px-2 py-1 shadow-2xl safe-area-bottom">
          <Link
            to="/"
            className={cn(
              "flex flex-col items-center justify-center py-1.5 px-2 rounded-xl text-[10px] font-bold transition min-w-[54px]",
              location.pathname === '/' ? "text-red-500 bg-red-950/40" : "text-slate-400 hover:text-slate-200"
            )}
          >
            <LayoutDashboard className="w-5 h-5 mb-0.5" />
            <span className="leading-tight">Início</span>
          </Link>
          <Link
            to="/agenda"
            className={cn(
              "flex flex-col items-center justify-center py-1.5 px-2 rounded-xl text-[10px] font-bold transition min-w-[54px]",
              location.pathname.startsWith('/agenda') ? "text-red-500 bg-red-950/40" : "text-slate-400 hover:text-slate-200"
            )}
          >
            <CalendarDays className="w-5 h-5 mb-0.5" />
            <span className="leading-tight">Agenda</span>
          </Link>
          <Link
            to="/os"
            className={cn(
              "flex flex-col items-center justify-center py-1.5 px-2 rounded-xl text-[10px] font-bold transition min-w-[54px]",
              location.pathname.startsWith('/os') ? "text-red-500 bg-red-950/40" : "text-slate-400 hover:text-slate-200"
            )}
          >
            <FileText className="w-5 h-5 mb-0.5" />
            <span className="leading-tight">O.S</span>
          </Link>
          <Link
            to="/propostas"
            className={cn(
              "flex flex-col items-center justify-center py-1.5 px-2 rounded-xl text-[10px] font-bold transition min-w-[54px]",
              location.pathname.startsWith('/propostas') ? "text-red-500 bg-red-950/40" : "text-slate-400 hover:text-slate-200"
            )}
          >
            <ClipboardList className="w-5 h-5 mb-0.5" />
            <span className="leading-tight">Propostas</span>
          </Link>
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className={cn(
              "flex flex-col items-center justify-center py-1.5 px-2 rounded-xl text-[10px] font-bold transition min-w-[54px] cursor-pointer",
              sidebarOpen ? "text-red-400 bg-red-950/40" : "text-slate-400 hover:text-slate-200"
            )}
          >
            <Menu className="w-5 h-5 mb-0.5" />
            <span className="leading-tight">Mais</span>
          </button>
        </nav>
      </div>
    </div>
  );
}

